import type { PushSubscriptionPayload } from '../types/notification'
import type { WorkOrder } from '../types/workorder'
import { api } from './fetch'

export interface UnreadCountResponse {
  unread_count: number
}

export interface MarkReadResponse {
  success: boolean
  message: string
}

export interface PushPublicKeyResponse {
  public_key: string | null
}

export const notificationService = {
  // Tag notification methods
  async getUnreadTagsCount(): Promise<UnreadCountResponse> {
    const response = await api.get('/notifications/unread-tags-count/')
    return response.data
  },

  async markTagAsRead(tagId: number): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-tag-read/', {
      tag_id: tagId,
    })
    return response.data
  },

  async markAllTagsAsRead(): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-all-tags-read/')
    return response.data
  },

  // Project notification methods
  async getUnreadProjectsCount(): Promise<UnreadCountResponse> {
    const response = await api.get('/notifications/unread-projects-count/')
    return response.data
  },

  async markProjectAsRead(projectId: number): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-project-read/', {
      project_id: projectId,
    })
    return response.data
  },

  async markAllProjectsAsRead(): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-all-projects-read/')
    return response.data
  },

  // Work order notification methods
  async getUnreadWorkOrdersCount(): Promise<UnreadCountResponse> {
    const response = await api.get('/notifications/unread-workorders-count/')
    return response.data
  },

  async markWorkOrderAsRead(workOrderId: number): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-workorder-read/', {
      workorder_id: workOrderId,
    })
    return response.data
  },

  async markAllWorkOrdersAsRead(): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-all-workorders-read/')
    return response.data
  },

  // Split work order notification methods
  // Pending work orders (status: PENDING, NEW, or null)
  async getUnreadPendingWorkOrdersCount(): Promise<UnreadCountResponse> {
    const response = await api.get(
      '/notifications/unread-pending-workorders-count/',
    )
    return response.data
  },

  async getUnreadPendingWorkOrders(): Promise<WorkOrder[]> {
    const response = await api.get('/notifications/unread-pending-workorders/')
    return response.data
  },

  async markAllPendingWorkOrdersAsRead(): Promise<MarkReadResponse> {
    const response = await api.post(
      '/notifications/mark-all-pending-workorders-read/',
    )
    return response.data
  },

  // Accepted work orders (status: ACCEPTED)
  async getUnreadAcceptedWorkOrdersCount(): Promise<UnreadCountResponse> {
    const response = await api.get(
      '/notifications/unread-accepted-workorders-count/',
    )
    return response.data
  },

  async getUnreadAcceptedWorkOrders(): Promise<WorkOrder[]> {
    const response = await api.get('/notifications/unread-accepted-workorders/')
    return response.data
  },

  async markAllAcceptedWorkOrdersAsRead(): Promise<MarkReadResponse> {
    const response = await api.post(
      '/notifications/mark-all-accepted-workorders-read/',
    )
    return response.data
  },

  // Invoice notification methods
  async getUnreadInvoicesCount(): Promise<UnreadCountResponse> {
    const response = await api.get('/notifications/unread-invoices-count/')
    return response.data
  },

  async markInvoiceAsRead(invoiceId: number): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-invoice-read/', {
      invoice_id: invoiceId,
    })
    return response.data
  },

  async markAllInvoicesAsRead(): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/mark-all-invoices-read/')
    return response.data
  },

  // Web push notification methods
  async getPushPublicKey(): Promise<PushPublicKeyResponse> {
    const response = await api.get('/notifications/push/public-key/')
    return response.data
  },

  async subscribeToPush(
    subscription: PushSubscriptionPayload,
  ): Promise<MarkReadResponse> {
    const response = await api.post(
      '/notifications/push/subscribe/',
      subscription,
    )
    return response.data
  },

  async unsubscribeFromPush(endpoint: string): Promise<MarkReadResponse> {
    const response = await api.post('/notifications/push/unsubscribe/', {
      endpoint,
    })
    return response.data
  },
}
